import * as moment from 'moment';
import { IStrongBoxOpoFilter, IStrongBoxOpoUsageListing } from "@nkr/models/strong-box";
import { Utility } from "@nkr/services/utility";
import { useMemo, useRef, useState } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import { LoadingIndicator } from "@nkr/components/loading-indicator";
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClassParams, ValueGetterParams } from "ag-grid-community";
import { GridWrapper } from '../strong-box/styles/styles'
import { AgGridReact } from "ag-grid-react";
import { TLegendEntry } from "../strong-box/legend";
import { useAppHookContext } from '@nkr/utils/rest-api';
import useStrongBoxOpoUsages from "@nkr/store/strong-box-opo/hooks/use-strong-box-opo-usages";
import SortableHeaderComponent from "../recipients/recipient-shield/components/sortable-header";
import StrongBoxFilter from "../strong-box/strong-box-filter";
import DonorCenterCell from "../strong-box/donor-center-cell";

const StrongBoxOpoUsages = () => {
    const { app: { user } } = useAppHookContext();
    
    const defaultFilter: IStrongBoxOpoFilter = {
        startDate: Utility.getFirstOfCurrentMonth(),
        endDate: Utility.getLastOfCurrentMonth(),
        isCenter: false,
        opoId: user.opoId == 0 ? null : user.opoId
    }

    const [filter, setFilter] = useState<IStrongBoxOpoFilter>(defaultFilter);
    const { list, loading, error } = useStrongBoxOpoUsages(filter);

    const colDefs: ColDef[] = [
        {
            field: 'opoName',
            rowGroup: !user.opoId ? true : false,
            hide: true,
            showRowGroup: true
        },
        {
            colId: 'opoId',
            field: 'opoId',
            hide: true,
            aggFunc: 'max'
        },
        {
            colId: 'centerId',
            field: 'centerId',
            hide: true,
            aggFunc: 'max'
        },
        {
            colId: 'compositeSwapNumber',
            field: 'swapInfo.compositeSwapNumber',
            headerName: 'Case #',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Case</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoUsageListing = params.data;
                return <>{d.swapInfo?.compositeSwapNumber || 'NA'}</>;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoUsageListing = params.data;
                return d.swapInfo?.compositeSwapNumber || 'NA';
            },
        },
        {
            colId: 'donorCenter',
            field: 'swapInfo.donorCenter',
            headerName: 'Donor Center',
            width: 140,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoUsageListing = params.data;
                return <DonorCenterCell donorCenter={d.swapInfo?.donorCenter} remoteDonorCenter={d.swapInfo?.remoteDonorCenter} />;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoUsageListing = params.data;
                return d.swapInfo?.donorCenter || 'NA';
            },
        },
        {
            colId: 'recipientCenter',
            field: 'swapInfo.recipientCenter',
            headerName: 'Recipient Center',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoUsageListing = params.data;
                return <>{d.swapInfo?.recipientCenter || 'NA'}</>;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoUsageListing = params.data;
                return d.swapInfo?.recipientCenter || 'NA';
            },
        },
        {
            colId: 'kidneyPickupDate',
            field: 'swapInfo.kidneyPickupDate',
            headerName: 'Pickup Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Pickup Date</SortableHeaderComponent>,
        },
        {
            colId: 'createdDate',
            field: 'createdDate',
            headerName: 'Usage Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Usage Date</SortableHeaderComponent>,
        },
    ];

    const columnDefs = useMemo(() => colDefs, []);

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const expCsv = () => {
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: `StrongBoxOpoUsageHistory.csv`,
                columnKeys: ['compositeSwapNumber', 'opoName', 'donorCenter', 'recipeintCenter', 'kidneyPickupDate', 'createdDate'],
            });
        }
    };

    const getRowStyle = (params: RowClassParams) => {
        const d: IStrongBoxOpoUsageListing = params.data;
        if (d?.swapInfo?.isSwapCancelled) {
            return { background: '#D79E9B' };
        }
    }

    const legendEntries: TLegendEntry[] = [
        { text: 'Cancelled Swap', color: '#D79E9B' }
    ];

    return (
        <>
            <Row>
                <Col sm={12}>
                    <StrongBoxFilter
                        filter={filter}
                        defaultFilter={defaultFilter}
                        startDateLabel="Earliest Usage Date"
                        endDateLabel="Latest Usage Date"
                        legendEntries={legendEntries}
                        isCenter={false}
                        onChange={setFilter}
                        onExport={expCsv}
                    />
                </Col>
            </Row>

            {loading && <LoadingIndicator type='spin' color='black' loading={true} message={"Loading Usages ..."} />}

            {error && <Alert bsStyle="danger">{JSON.stringify(error)}</Alert>}

            {!loading &&
                <Row>
                    <Col sm={12}>
                        <GridWrapper className='ag-theme-bootstrap'>
                            <AgGridReact
                                defaultColDef={{
                                    headerClass: 'list-header',
                                    cellClass: 'list-cell',
                                    sortable: true
                                }}
                                columnDefs={columnDefs}
                                rowData={list}
                                headerHeight={30}
                                rowHeight={32}
                                noRowsOverlayComponentFramework={() => <div>No OPO usage history records were found...</div>}
                                suppressRowClickSelection={true}
                                suppressCellSelection={true}
                                enableCellTextSelection={true}
                                groupDisplayType={'groupRows'}
                                groupDefaultExpanded={1}
                                groupSelectsChildren={true}
                                onGridReady={onGridReady}
                                onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                                onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                                onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                                getRowStyle={getRowStyle}
                                // groupRowRendererParams={{
                                //     innerRendererFramework: GroupRowInnerRenderer,
                                //     innerRendererParams: {
                                //         // onAssignCenters: (e: FormEvent<HTMLButtonElement>) => {
                                //         //     let opoId = parseInt(e.currentTarget.value);
                                //         //     let d = list.find(x => x.opoId == opoId);

                                //         //     setAssignCenters({
                                //         //         show: true,
                                //         //         opoId,
                                //         //         opoName: d.opoName
                                //         //     });
                                //         // }
                                //     },
                                //     suppressCount: true,
                                // }}
                                groupRowRendererParams={{
                                    suppressCount: true,
                                }}
                            />
                        </GridWrapper>
                    </Col>
                </Row>
            }
        </>
    );
}

export default StrongBoxOpoUsages
